import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase, resilientFetch } from '../utils/supabaseClient'; // Use centralized client
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../utils/env';

// Export the centralized supabase client
export { supabase };

const CustomerContext = createContext();

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
};

export const CustomerProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [customerNotFound, setCustomerNotFound] = useState(false); // Auth succeeded but no portal account
  const [loadError, setLoadError] = useState(null); // Lookup FAILED — distinct from "no account"
  const [loading, setLoading] = useState(false); // Never block initial page load
  const [authResolved, setAuthResolved] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [session, setSession] = useState(null);
  const loadingRef = useRef(false);
  // auth user id whose customer record is fully loaded (null when none).
  const loadedAuthUserIdRef = useRef(null);
  // The signup fallback object has no portal_user_id, and a load that set loadError is only
  // partial — leave both eligible for a real reload, as the re-emitted SIGNED_IN did before.
  useEffect(() => {
    loadedAuthUserIdRef.current = customer?.portal_user_id && !loadError ? customer.auth_user_id : null;
  }, [customer, loadError]);

  const loadCustomerData = useCallback(async (authUser) => {
    // Prevent concurrent calls
    if (loadingRef.current) {
      console.log('🔍 loadCustomerData already in progress, skipping...');
      return;
    }
    
    loadingRef.current = true;
    
    try {
      console.log('🔍 Loading customer data for user:', authUser.email);

      // Simple query - no timeout needed, let it fail naturally
      const { data: accounts, error } = await supabase
        .from('portal_users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .eq('is_active', true);

      if (error) {
        // Do NOT return silently here. This used to leave `customer`, `customerNotFound`
        // and `loadError` all untouched, and `loading` starts false — so an RLS denial or
        // a network failure was indistinguishable from "no portal account", and every page
        // that gates on `customer?.customer_id` simply never loaded and never explained why.
        console.error('Error loading customer data:', error);
        setCustomer(null);
        setCustomerNotFound(false);
        setLoadError(error.message || 'Could not load your account. Please try again.');
        return;
      }
      setLoadError(null);

      if (accounts && accounts.length > 0) {
        // `portal_users` has no unique constraint on auth_user_id, and PostgREST row order
        // without an ORDER BY is arbitrary — so if one auth user ever gained two active
        // rows pointing at different customers, the portal would bind to whichever came
        // back first and could switch between page loads. Verified live 2026-08-24: no
        // auth user currently has more than one active row, so this is a latent hazard,
        // not a live bug. Ordering by created_at makes the pick deterministic in the
        // meantime; the real fix is a partial unique index (tracked as B7).
        const account = [...accounts].sort(
          (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
        )[0];

        if (accounts.length > 1) {
          console.warn(
            `[portal] ${accounts.length} active portal_users rows for this auth user — ` +
            `binding to the oldest (${account.id}). This should not happen; see audit B7.`
          );
        }

        // Reset customerNotFound when customer is found
        setCustomerNotFound(false);

        // Get customer data if available
        let customer = null;
        if (account.customer_id) {
          try {
            const customerResult = await supabase
              .from('customers')
              .select('*')
              .eq('id', account.customer_id)
              .single();

            if (!customerResult.error) {
              customer = customerResult.data;
            } else {
              // Swallowing this used to render an apparently-valid profile whose name was
              // the literal string 'Customer' and whose every address field was undefined.
              // Surface it instead — the portal_users row still lets them sign in, so this
              // is a degraded state, not a dead one.
              console.error('❌ Customer record fetch failed:', customerResult.error);
              setLoadError('We could not load your profile details. Some information may be missing.');
            }
          } catch (customerError) {
            console.error('❌ Customer data fetch failed:', customerError);
            setLoadError('We could not load your profile details. Some information may be missing.');
          }
        }

        // Build customer context data
        let displayName = 'Customer';
        if (customer) {
          const firstName = customer.first_name || '';
          const lastName = customer.last_name || '';
          const fullName = `${firstName} ${lastName}`.trim();
          if (fullName) {
            displayName = fullName;
          }
        }

        const customerData = {
          // `id` is the PORTAL_USERS row id, NOT the customers id. Those are different
          // tables and the distinction has already caused bugs (Profile.js updates
          // portal_users WHERE id = customer.id, while the signup path below used to set
          // `id` to a customers.id). `portal_user_id` is the unambiguous name — prefer it;
          // `id` is kept only so existing call sites keep working.
          id: account.id,
          portal_user_id: account.id,
          customer_id: account.customer_id,
          auth_user_id: authUser.id,
          email: authUser.email || account.email,
          name: displayName,
          phone: customer?.phone,
          // Read by Profile.js. Omitting it made both notification toggles render checked
          // no matter what was stored, and made the save spread `{}` — overwriting the
          // whole jsonb instead of merging into it.
          communication_preferences: customer?.communication_preferences || null,
          street_address: customer?.billing_address_line_1,
          city: customer?.billing_city,
          state: customer?.billing_state,
          zip_code: customer?.billing_zip_code,
          customer_type: customer?.type,
          // needs_password_setup / last_login_at / created_via are NOT columns on
          // portal_users (it holds only id, customer_id, auth_user_id, email, role,
          // is_active, created_at, updated_at). select('*') made them silently
          // undefined, which broke both consumers in opposite directions.
          created_at: account.created_at
        };

        setCustomer(customerData);
        console.log('✅ Customer data loaded successfully');
        return customerData;
      } else {
        console.log('📋 No customer portal account found for user:', authUser.email);
        setCustomer(null);
        setCustomerNotFound(true);
      }
    } catch (error) {
      console.error('❌ Error loading customer data:', error);
      setCustomer(null);
    } finally {
      loadingRef.current = false;
    }
  }, []);

  // Read session ONCE on boot - simple and fast
  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log('📊 Session found on boot:', session.user.email);
          setSession(session);
          setIsAuthenticated(true);
          // Load customer data in background - don't block UI
          loadCustomerData(session.user);
        } else {
          console.log('📊 No session found on boot');
        }
      } catch (error) {
        console.error('❌ Session check failed:', error);
      } finally {
        // Always resolve auth - never block UI
        setAuthResolved(true);
      }
    };

    init();
  }, [loadCustomerData]);

  // Only listen for auth state changes - minimal handling
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔍 Auth state change:', event, session?.user?.email || 'no user');
      
      if (event === 'SIGNED_IN' && session) {
        setSession(session);
        setIsAuthenticated(true);
        // supabase-js re-emits SIGNED_IN on tab focus/session recovery. Re-reading
        // portal_users + customers for the SAME user that is already loaded is waste;
        // a different user, or no customer loaded yet, still loads.
        if (loadedAuthUserIdRef.current === session.user.id) return;
        // Load customer data in background
        loadCustomerData(session.user);
      } else if (event === 'SIGNED_OUT') {
        console.log('📋 User signed out - clearing state');
        setCustomer(null);
        setIsAuthenticated(false);
        setSession(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [loadCustomerData]);

  const logout = useCallback(async () => {
    try {
      console.log('🔧 Logging out user...');

      // Clear Supabase session
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
      }

      // Clear local state
      setCustomer(null);
      setCustomerNotFound(false);
      setIsAuthenticated(false);
      setSession(null);

      console.log('✅ User logged out successfully');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, []);

  // Magic link login (primary method)
  const loginWithMagicLink = useCallback(async (email) => {
    try {
      setLoading(true);

      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) throw error;

      setLoading(false);
      return { success: true, message: 'Magic link sent to your email!' };
    } catch (error) {
      console.error('Magic link error:', error);
      setLoading(false);
      return { success: false, error: error.message };
    }
  }, []);

  // Password login using Supabase Auth
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);

      // Use Supabase Auth for login
      const { error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      if (error) throw error;

      // Auth state change will handle the rest
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
      return { success: false, error: error.message };
    }
  }, []);

  // Self-signup for new customers (network hub functionality)
  // Uses Edge Function to bypass RLS and handle auth + customer creation server-side
  const selfSignup = useCallback(async (customerData) => {
    try {
      setLoading(true);

      // Step 1: Create Supabase Auth user (client-side)
      console.log('🔧 Creating Supabase Auth user for:', customerData.email);
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: customerData.email,
        password: customerData.password || 'TempPassword123!',
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
          data: {
            first_name: customerData.first_name,
            last_name: customerData.last_name
          }
        }
      });

      console.log('📊 Auth signup result:', { authData, authError });

      if (authError) {
        if (authError.message.includes('User already registered')) {
          // Do NOT silently sign the existing user in. Auto-signing-in on a signup attempt
          // discarded the name the user just entered and showed a confusing "Welcome back",
          // making a signup behave like a login. Direct them to the login page instead.
          // (F-ORION-0729-0955)
          const err = new Error('An account with this email already exists. Please log in instead.');
          err.code = 'EMAIL_EXISTS';
          throw err;
        }
        throw authError;
      }
      const authUser = authData.user;

      // Step 2: Call Edge Function to create customer + portal account (server-side with service role).
      // SECURITY (F-NOVA-0731): send the NEW USER'S session token — the edge fn derives identity from
      // this validated JWT, not from the body. (Was: passing the anon key + body-supplied identity,
      // which let anyone mint customers.) Requires a session (auto-confirm signup provides one).
      const accessToken = authData.session?.access_token;
      if (!accessToken) {
        throw new Error('Account created — please log in to finish setting up your portal.');
      }
      console.log('🔧 Calling portal-signup Edge Function...');
      const edgeFunctionResponse = await resilientFetch(
        `${SUPABASE_URL}/functions/v1/portal-signup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            first_name: customerData.first_name,
            last_name: customerData.last_name,
            // portal-signup refuses (400) without this, before it creates anything.
            // Sent explicitly as a boolean rather than passing customerData through, so
            // a truthy string can never stand in for a ticked box.
            accepted_legal: customerData.accepted_legal === true
          })
        }
      );

      if (!edgeFunctionResponse.ok) {
        const errorData = await edgeFunctionResponse.json();
        console.error('❌ Edge function error:', errorData);
        throw new Error(errorData.error || 'Failed to create customer account');
      }

      const responseData = await edgeFunctionResponse.json();
      console.log('📊 Raw Edge function response:', responseData);

      const { customer } = responseData;

      if (!customer) {
        console.error('❌ Edge function returned incomplete data:', responseData);
        throw new Error('Edge function returned incomplete data - missing customer');
      }

      console.log('✅ Edge function succeeded:', { customer });

      // Step 3: Force refresh customer data now that the portal account exists
      console.log('🔧 Force refreshing customer data after signup...');
      let loaded = null;
      if (authData.session) {
        try {
          loaded = await loadCustomerData(authUser);
          console.log('✅ Customer data refreshed after signup');
        } catch (refreshError) {
          console.error('❌ Failed to refresh customer data after signup:', refreshError);
          // Continue anyway - we'll set basic context data below
        }
      }

      // Step 4: fallback context, ONLY if the real load above did not produce one.
      //
      // This used to run unconditionally and overwrite the correct object — and it set
      // `id` to a customers.id, while the logged-in path sets `id` to a portal_users.id.
      // So `customer.id` meant one thing immediately after signup and a different thing
      // after the next page load, and Profile.js (which does
      // `update portal_users where id = customer.id`) matched nothing until you refreshed.
      if (!loaded) {
        setCustomer({
          id: null,              // no portal_users row resolved yet — deliberately null
          portal_user_id: null,  // rather than a customers id masquerading as one
          customer_id: customer.id,
          auth_user_id: authUser.id,
          email: customerData.email,
          name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Customer',
          phone: customer.phone,
          communication_preferences: customer.communication_preferences || null,
          street_address: customer.billing_address_line_1,
          city: customer.billing_city,
          state: customer.billing_state,
          zip_code: customer.billing_zip_code,
          customer_type: customer.type,
          created_at: customer.created_at
        });
      }
      setIsAuthenticated(true);
      setSession(authData.session);

      console.log('✅ Customer context set - user should stay logged in');

      return {
        success: true,
        message: authData.session ?
          'Account created successfully! You are now logged in.' :
          'Account created successfully! Please check your email to verify your account.',
        user: authUser,
        customer: customer,
        requiresEmailVerification: !authData.session
      };
    } catch (error) {
      console.error('Self signup error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadCustomerData]);

  // Legacy service request functions - kept for backward compatibility
  // New requests should use marketplace tables directly via supabase client
  const submitServiceRequest = useCallback(async (requestData) => {
    console.warn('submitServiceRequest is deprecated. Use marketplace tables directly.');
    // This function is kept for backward compatibility but should not be used for new requests
    return null;
  }, []);

  const loadServiceRequests = useCallback(async () => {
    console.warn('loadServiceRequests is deprecated. Use marketplace tables directly.');
    // This function is kept for backward compatibility but should not be used
    return [];
  }, []);

  const loadServiceRequestResponses = useCallback(async (requestId) => {
    console.warn('loadServiceRequestResponses is deprecated. Use marketplace tables directly.');
    // This function is kept for backward compatibility but should not be used
    return [];
  }, []);

  /** Re-read the signed-in customer. Call after a write that changes profile data. */
  const refreshCustomer = useCallback(async () => {
    if (!session?.user) return null;
    return loadCustomerData(session.user);
  }, [session, loadCustomerData]);

  const value = useMemo(() => ({
    customer,
    customerNotFound,
    loadError, // lookup FAILED — distinct from customerNotFound ("no portal account")
    loading,
    authResolved,
    isAuthenticated,
    session,
    login,
    loginWithMagicLink,
    logout,
    selfSignup,
    refreshCustomer,
    submitServiceRequest,
    loadServiceRequests,
    loadServiceRequestResponses,
    supabase // Expose supabase client for direct use if needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [customer, customerNotFound, loadError, loading, authResolved, isAuthenticated, session, login, loginWithMagicLink, logout, selfSignup, refreshCustomer, submitServiceRequest, loadServiceRequests, loadServiceRequestResponses]);

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export { CustomerContext };
